import { createContext, memo, useContext } from 'react'
import dynamic from 'next/dynamic'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import type { tProduct } from 'src/shared/frontendTypes/product'
import type { combination } from './functions/getCombination'
import type { selectedAttributes } from './Components/AddToCart/Components/AttributesSelector'
import { useProductRecommendations } from 'src/hooks/useProductRecommendations'
import { useRecommendationReveal } from './useRecommendationReveal'
import { useRecommendationAnchor } from './useRecommendationAnchor'
import { pageBuilderOutlinedButtonSx } from 'src/theme/pageBuilderButtons'
import { PurchasedAccordion } from './Components/DescriptionAccordion/DescriptionAccordion'
import { CrossSellRecommendationShell } from './Components/RecommendationShell'
import type { RecommendationLayoutSeed } from './functions/recommendationLayoutSeed'

const LayoutContext = createContext<RecommendationLayoutSeed | undefined>(undefined)
const CrossSellChunkPlaceholder = ({ error }: { error?: Error | null }) => {
  const seed = useContext(LayoutContext)
  return <CrossSellRecommendationShell tabs={seed?.crossSellTabs || []} loading={!error}
    status={error ? 'Empfehlungen konnten nicht geladen werden. Bitte lade die Seite erneut.' : undefined} />
}
const CrossSellSections = dynamic(() => import('./Components/CrossSellSections/CrossSellSections'), {
  loading: CrossSellChunkPlaceholder,
})

// Recommendation responses and the reveal state only update this region. The
// main product, its schema and purchase controls do not depend on that request.
const ProductRecommendations: React.FC<{
  product: tProduct
  deferred: boolean
  combination: combination | null
  selectedAttributes: selectedAttributes
  recommendationsLayoutSeed?: RecommendationLayoutSeed
}> = ({ product, deferred, combination, selectedAttributes, recommendationsLayoutSeed: seed }) => {
  const options = combination?.options?.length
    ? combination.options
    : Object.values(selectedAttributes).filter((value): value is number => typeof value === 'number')
  const seededCrossSell = Boolean(seed && (seed.crossSellTabs.length || seed.hasPotentialCrossSell))
  const knownEmpty = Boolean(seed && !seed.purchasedCount && !seededCrossSell)
  const recommendations = useProductRecommendations(product.id, options, deferred && !knownEmpty)
  const layout = useRecommendationReveal(deferred, Boolean(seed || recommendations.data))
  useRecommendationAnchor(layout.anchorRef)
  const crossSellSections = deferred ? recommendations.data?.crossSellSections || [] : product.crossSellSections
  const alsoPurchased = deferred ? recommendations.data?.alsoPurchased || [] : product.alsoPurchased
  // The small SSR seed fixes the slots for this cached page. A later editorial
  // addition receives a new seed during normal ISR, without inserting a new row
  // into an already-read page. Prices and the current selection remain live.
  const purchasedCount = seed ? seed.purchasedCount : alsoPurchased?.length || 0
  const hasCrossSellSlot = seed ? seededCrossSell : !deferred || Boolean(
    crossSellSections?.some(section => section.products.length)
    || recommendations.data?.dynamicSerie?.section?.products.length,
  )
  const readyToRender = !deferred || (Boolean(recommendations.data) && layout.showRecommendations)
  const error = recommendations.error ? <>
    <Typography>Empfehlungen konnten nicht geladen werden.</Typography>
    <Button variant='outlined' sx={pageBuilderOutlinedButtonSx} onClick={recommendations.retry}>
      Erneut versuchen
    </Button>
  </> : undefined

  return <div ref={layout.anchorRef} style={{ display: 'flow-root' }}>
    <Box aria-label='Produktempfehlungen' aria-busy={recommendations.loading}>
      {deferred && purchasedCount > 0 && <PurchasedAccordion
        product={product}
        alsoPurchased={readyToRender ? alsoPurchased : []}
        expectedCount={purchasedCount}
        loading={!readyToRender && !recommendations.error}
        status={error}
      />}
      {hasCrossSellSlot && (readyToRender ? <LayoutContext.Provider value={seed}>
        <CrossSellSections
          key={product.id}
          product={product}
          crossSellSections={crossSellSections}
          initialDynamicSerie={recommendations.data?.dynamicSerie}
          recommendationsReady={!deferred || Boolean(recommendations.data)}
          recommendationsLayoutSeed={seed}
          selectedAttributes={selectedAttributes}
          combination={combination}
        />
      </LayoutContext.Provider> : <CrossSellRecommendationShell
        tabs={seed?.crossSellTabs || []}
        loading={!recommendations.error}
        status={error}
      />)}
      {!seed && deferred && !purchasedCount && !hasCrossSellSlot && <Box sx={{ minHeight: 64, py: 1 }}>
        {error || <Typography role='status'>{recommendations.loading
          ? 'Empfehlungen werden geladen …'
          : 'Keine weiteren Empfehlungen verfügbar.'}</Typography>}
      </Box>}
    </Box>
  </div>
}

export default memo(ProductRecommendations)
